import { HIDE_GROUPS, type HideGroup } from "./bigTypes";
import type { TowerKit } from "./types";

/**
 * Which stock parts a big kit may hide (kit/bigTypes.ts HideGroup), as pure tables: SkinSlots.tag writes each vertex's
 * group into the one-byte `tdHide` attribute at load, and the kit's body material (kit/bodyMaterial.ts) and its shadow
 * depth twin (kit/hideMaterial.ts) collapse the vertices whose group the kit hides. A part's whole triangles share its
 * bone, so a group is always whole triangles (a finial welded into its roof goes with the roof: there is no finer cut
 * that does not tear the mesh).
 *
 * Never hidden: the element parts and the `_glow` meshes (readability: the element glow says what a tower is), the
 * drum, the weapon, and any part something important stands on (HIDE_REFUSED) — that stays, and the kit's
 * replacement for it is not mounted there (`ifHidden`).
 */

/** Part name (canonical: kit/slots.ts canonicalPart) → hide group. Unlisted parts are never hidden. */
export const HIDE_PARTS: Readonly<Record<string, HideGroup>> = {
  roof: "roof",
  flag: "flags",
  deck: "gallery",
  scaffold: "scaffold",
  lamp: "lamps",
  door: "doors", door_l: "doors", door_r: "doors",
  plinth: "plinth",
};

/**
 * Per tower type: groups that stay whatever a kit asks, and why (checked on every tower × tier, t4 branches included,
 * on the capture sheets of 2026-10-21):
 *  - lightning `roof`: the coil stack and the crystal spire stand inside the roof storey; without it they float.
 *  - frost `roof` (only the blizzard branch has one): its ice spire is the frost element (kit/slots.ts SLOT_OVERRIDES).
 *  - the gallery carries the weapon or the element on every tower but the arcane spire and the hex totem (archer's bow,
 *    cannon and ballista mounts, frost crystal, fire brazier, poison brew, beacon orb, mortar barrel, barracks hall).
 */
export const HIDE_REFUSED: Readonly<Record<string, readonly HideGroup[]>> = {
  lightning: ["roof", "gallery"], frost: ["roof", "gallery"],
  archer: ["gallery"], cannon: ["gallery"], fire: ["gallery"], ballista: ["gallery"], poison: ["gallery"],
  beacon: ["gallery"], mortar: ["gallery"], barracks: ["gallery"],
};

/** The bit of a group in a hide mask and in `tdHide` (its index + 1; 0 = never hidden). */
export function hideBit(group: HideGroup): number { return HIDE_GROUPS.indexOf(group) + 1; }

/** A kit's hide list as a mask (bit n set = group with bit n hidden). */
export function hideMask(groups: readonly HideGroup[] | undefined): number {
  let m = 0;
  for (const g of groups ?? []) m |= 1 << hideBit(g);
  return m;
}

/** The mask a kit asks to hide on tower type `tower` (its `hideFit` for that tower, else its `hide`). */
export function kitHideMask(kit: Pick<TowerKit, "hide" | "hideFit"> | null | undefined, tower: string): number {
  return hideMask(kit?.hideFit?.[tower] ?? kit?.hide);
}

/** The tower type of a tower model name (`tower_archer_t4_ranger__ivory` → archer), or null. */
export function towerOfModel(model: string): string | null {
  return /^tower_([a-z]+)_t\d/.exec(model)?.[1] ?? null;
}

/** The hide group (its bit; 0 = never hidden) of a canonical part on a tower type. */
export function hideGroupOf(tower: string | null, part: string | null): number {
  const g = part ? HIDE_PARTS[part] : undefined;
  if (!g) return 0;
  if (tower && HIDE_REFUSED[tower]?.includes(g)) return 0;
  return hideBit(g);
}

/** The mask of groups that can be hidden on a tower whose model has these (canonical) parts. */
export function hideableMask(tower: string | null, parts: Iterable<string | null>): number {
  let m = 0;
  for (const p of parts) { const b = hideGroupOf(tower, p); if (b) m |= 1 << b; }
  return m;
}

/** Whether `group` is hidden under mask `hidden` (a kit's mask already ANDed with what the model can hide). */
export function isHidden(hidden: number, group: HideGroup): boolean { return (hidden & (1 << hideBit(group))) !== 0; }
